import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta, useHead } from '@unhead/react';
import { Plus, Search, Filter, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoginArea } from '@/components/auth/LoginArea';
import { AppCard } from '@/components/AppCard';
import { Logo } from '@/components/Logo';
import { useApps } from '@/hooks/useApps';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { CATEGORIES, type AppCategory } from '@/lib/appTypes';

const Index = () => {
  // Get the current URL dynamically for OG tags
  const currentUrl = typeof window !== 'undefined' ? window.location.origin : 'https://nostrstore.com';
  const ogImageUrl = `${currentUrl}/og-image.jpg`;
  
  useSeoMeta({
    title: 'NostrStore.com - Decentralized App Marketplace',
    description: 'Discover and share decentralized applications on Nostr. The premier marketplace for Nostr apps, built by the community.',
    ogTitle: 'NostrStore.com - Decentralized App Marketplace',
    ogDescription: 'Discover and share decentralized applications on Nostr',
    ogImage: ogImageUrl,
    ogUrl: currentUrl,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'NostrStore.com - Decentralized App Marketplace',
    twitterDescription: 'Discover and share decentralized applications on Nostr',
    twitterImage: ogImageUrl,
  });

  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: apps, isLoading } = useApps({
    category: selectedCategory || undefined,
  });

  // Filter apps by search query
  const filteredApps = apps?.filter((app) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      app.name.toLowerCase().includes(query) ||
      app.description.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-black/95 relative overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-800/10 via-purple-900/5 to-transparent opacity-50" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-[40rem] h-[40rem] bg-purple-600/5 rounded-full mix-blend-normal filter blur-[100px] opacity-70 animate-blob" style={{ animationDuration: '20s' }} />
          <div className="absolute top-[20%] -right-4 w-[35rem] h-[35rem] bg-purple-800/5 rounded-full mix-blend-normal filter blur-[100px] opacity-70 animate-blob" style={{ animationDuration: '25s', animationDelay: '2s' }} />
          <div className="absolute -bottom-8 left-[20%] w-[45rem] h-[45rem] bg-purple-700/5 rounded-full mix-blend-normal filter blur-[100px] opacity-70 animate-blob" style={{ animationDuration: '30s', animationDelay: '4s' }} />
        </div>
      </div>

      {/* Header */}
      <div className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 backdrop-blur-sm bg-black/20 rounded-2xl p-4 sm:p-6 border border-purple-500/20">
            <div className="flex items-center gap-3 sm:gap-4">
              <Logo size={56} />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-200 via-blue-200 to-purple-200 bg-clip-text text-transparent animate-gradient">
                  NostrStore.com
                </h1>
                <p className="text-xs sm:text-sm text-purple-300/70 flex items-center gap-1">
                  <Store className="h-3 w-3" />
                  Decentralized App Marketplace
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <LoginArea className="flex-1 sm:flex-initial" />
              {user && (
                <Button
                  onClick={() => navigate('/add')}
                  className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-purple-50 border-0 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/40 transition-all"
                >
                  <Plus className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Add App</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search and Filters */}
        <div className="mb-6 sm:mb-8 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-purple-300/50" />
            <Input
              type="text"
              placeholder="Search apps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black/30 border-purple-500/20 text-purple-50 placeholder:text-purple-300/30 focus:border-purple-500/40 h-11 sm:h-12"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <Badge
              variant={selectedCategory === '' ? 'default' : 'outline'}
              className={`cursor-pointer whitespace-nowrap transition-all ${
                selectedCategory === ''
                  ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700'
                  : 'bg-purple-500/10 text-purple-200/90 border-purple-500/20 hover:bg-purple-500/20'
              }`}
              onClick={() => setSelectedCategory('')}
            >
              All
            </Badge>
            {CATEGORIES.map((category) => (
              <Badge
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={`cursor-pointer whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700'
                    : 'bg-purple-500/10 text-purple-200/90 border-purple-500/20 hover:bg-purple-500/20'
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Badge>
            ))}
          </div>
        </div>

        {/* Apps Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="overflow-hidden bg-gradient-to-br from-black/40 via-purple-500/5 to-purple-900/10 border-purple-500/10">
                <Skeleton className="w-full h-48 rounded-none" />
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <Skeleton className="w-14 h-14 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredApps && filteredApps.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredApps.map((app) => (
              <AppCard key={`${app.author}:${app.id}`} app={app} />
            ))}
          </div>
        ) : (
          <div className="col-span-full">
            <Card className="border-dashed bg-gradient-to-br from-black/40 via-purple-500/5 to-purple-900/10 border-purple-500/20">
              <CardContent className="py-12 px-8 text-center">
                <div className="max-w-sm mx-auto space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Filter className="h-8 w-8 text-purple-300/50" />
                  </div>
                  <p className="text-purple-200/70">
                    {searchQuery
                      ? `No apps found matching "${searchQuery}"`
                      : selectedCategory
                      ? `No apps found in ${selectedCategory} category`
                      : 'No apps available. Be the first to add one!'}
                  </p>
                  {user && (
                    <Button
                      onClick={() => navigate('/add')}
                      variant="outline"
                      className="border-purple-500/20 text-purple-200 hover:bg-purple-500/10"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Your App
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 pb-8 text-center">
          <p className="text-purple-200/60 text-base flex items-center justify-center gap-2">
            Made With Love By{' '}
            <a
              href="https://uno.dog"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 transition-colors font-semibold underline decoration-purple-400/30 hover:decoration-purple-300"
            >
              Uno
            </a>
            <span className="text-pink-400 animate-pulse">❤️</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Index;
